import { useParams } from "react-router-dom";
import { useData } from "../context/DataContext";
import SEO from "../components/SEO";

export default function ExhibitionDetail() {
  const { id } = useParams();
  const { works, loading, error } = useData();

  // Find the exhibition from preloaded data
  const exhibition = works.find((ex) => ex.id === id);

  if (loading) {
    return (
      <div className="mt-10 ml-5 w-full flex flex-col animate-fadeInUp min-h-96 max-h-96 max-md:ml-0 max-md:w-full max-md:overflow-x-hidden">
        <div className="flex flex-col items-center justify-center flex-1 min-h-96">
          <div className="flex gap-1 mb-5">
            <div
              className="w-2 h-2 bg-black rounded-full animate-bounce"
              style={{ animationDelay: "-0.32s" }}
            ></div>
            <div
              className="w-2 h-2 bg-black rounded-full animate-bounce"
              style={{ animationDelay: "-0.16s" }}
            ></div>
            <div className="w-2 h-2 bg-black rounded-full animate-bounce"></div>
          </div>
          <p className="text-gray-600 text-sm m-0 tracking-wide">
            Loading work details...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-10 ml-5 w-full flex flex-col animate-fadeInUp max-md:ml-0 max-md:w-full max-md:overflow-x-hidden">
        <p>Error loading work details: {error}</p>
      </div>
    );
  }

  if (!exhibition) {
    return (
      <div className="mt-10 ml-5 w-full flex flex-col animate-fadeInUp max-md:ml-0 max-md:w-full max-md:overflow-x-hidden">
        Work not found
      </div>
    );
  }

  return (
    <>
      <SEO
        title={exhibition.title}
        description={`${exhibition.title} (${exhibition.year}) - ${exhibition.location}. Curated by ${exhibition.curator}. ${exhibition.description}`}
        url={`https://nathanportelli.com/works/${exhibition.id}`}
        image={exhibition.headerImage}
      />
      <div className="mt-10 ml-5 w-full flex flex-col animate-fadeInUp max-md:ml-0 max-md:w-full max-md:overflow-x-hidden">
        {/* Hero Section */}
        <div className="flex gap-15 mb-6 max-w-5xl max-md:flex-col max-md:gap-10">
          <div className="flex-1 flex flex-col justify-center">
            <h1 className="text-3xl font-normal m-0 mb-6 tracking-wider leading-tight">
              {exhibition.title}
            </h1>
            <div className="flex flex-col gap-2 mb-8">
              <span className="text-base text-gray-600 tracking-wide font-medium">
                {exhibition.year}
              </span>
              <span className="text-base text-gray-600 tracking-wide italic">
                {exhibition.location}
              </span>
              <span className="text-base text-gray-600 tracking-wide mt-1">
                Curated by {exhibition.curator}
              </span>
            </div>
            <p className="text-base leading-relaxed text-gray-800 m-0">
              {exhibition.description}
            </p>
          </div>
        </div>

        {/* Works Gallery */}
        <div className="max-w-5xl">
          <div className="grid grid-cols-1 gap-15">
            {exhibition.works.map((work, index) => (
              <div key={index} className="flex flex-col">
                <div
                  className={`w-full bg-gray-50 flex gap-3 mb-6 ${
                    work.column_layout ? "flex-row flex-wrap" : "flex-col"
                  }`}
                >
                  {work.images && work.images.length > 0 ? (
                    work.images.map((media, imgIndex) => {
                      // Check if the media is a video by file extension
                      const isVideo =
                        /\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)$/i.test(media);

                      return isVideo ? (
                        <video
                          key={imgIndex}
                          src={media}
                          controls
                          autoPlay
                          muted
                          playsInline
                          className={`h-auto object-contain block max-h-96 ${
                            work.column_layout ? "flex-1 min-w-0" : "w-full"
                          }`}
                          preload="metadata"
                        >
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <img
                          key={imgIndex}
                          src={media}
                          alt={`${exhibition.title} - Work ${index + 1} - ${
                            imgIndex + 1
                          }`}
                          className={`h-auto object-contain block max-h-96 ${
                            work.column_layout ? "flex-1 min-w-0" : "w-full"
                          }`}
                        />
                      );
                    })
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm">
                      No media
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm leading-relaxed text-gray-600 m-0">
                    {work.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
